import styles from "./DisplayDocument.module.css";
import downloadFile from "../../services/DownloadFile";
import DeleteModal from "../DeleteView/DeleteModal";
import React from "react";
import ElementsContextType from "../../types/ElementsContextType";
import { ElementsContext } from "../contexts/ElementsContext";
import { useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { LoggedInUserType } from "../../types/LoggedInUserType";
import getUserTypeText from "../../helpers/users";
import { useTranslation } from "react-i18next";

interface IDictionary {
    [index: string]: string;
}

const cardStyleList = {
    small: styles.smallCard,
    medium: styles.mediumCard,
    large: styles.largeCard,
    remaining: styles.remainingCard
} as IDictionary;

const documentStyleList = {
    small: styles.smallDocument,
    medium: styles.mediumDocument,
    large: styles.largeDocument,
    remaining: styles.remaining
} as IDictionary;

const filenameStyleList = {
    small: styles.smallFilename,
    medium: styles.mediumFilename,
    large: styles.largeFilename,
    remaining: styles.smallFilename
} as IDictionary;

export default function DisplayDocument(props: any) {
    const documentComplete: boolean = useSelector(
        (state: any) =>
            state.portal.activeInspection.SurveyorCompletionStatus
                .DocumentsComplete
    );
    const deleteSvg: string = documentComplete
        ? "/images/Roll-Over-Delete-Disable.svg"
        : "/images/Roll-Over-Delete.svg";
    const cursorStyle = documentComplete ? "default" : "pointer";
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const { openImageViewer } = React.useContext(
        ElementsContext
    ) as ElementsContextType;

    const { t } = useTranslation("common");

    const userType: LoggedInUserType = useSelector((state: any) => state.portal.userType);
    const assetUploader = getUserTypeText(userType);

    function assetUploadedBy(): string {
        return props.document?.metadata["asset-uploaded-by"] ?? null;
    }

    function onDownload(onClickEvent: any) {
        downloadFile(props.document.url, props.document.name);
        ReactGA.event({
            category: "Document Download",
            action: "download",
            label: props.document.name,
            value: 0
        });
        onClickEvent.stopPropagation();
    }

    function onDelete(onClickEvent: any) {
        if (!documentComplete) {
            setShowDeleteModal(true);
        }
        onClickEvent.stopPropagation();
    }

    function onView(onClickEvent: any) {
        openImageViewer(props.document, props.element);
        onClickEvent.stopPropagation();
    }

    function addedByFooter() {
        if (props.displayStyle === "large") {
            return (
                <div>
                    <p className={styles.addedByText}>
                        {t("inspection.documents.added_by")}{" "}
                        {assetUploadedBy() ?? assetUploader}
                    </p>
                </div>
            );
        }

        return null;
    }

    function getOnClickHandler(onClickEvent: any) {
        if (props.displayStyle === "large") {
            return onView(onClickEvent);
        }

        if (props.clickHandler) {
            return props.clickHandler(onClickEvent);
        }

        return onView(onClickEvent);
    }

    function documentName() {
        if (props.remaining && props.displayStyle !== "large") {
            return null;
        }

        return (
            <p className={filenameStyleList[props.displayStyle]}>
                {props.document.name}
            </p>
        );
    }

    const documentImageStyle = props.displayStyle == "medium" ? { backgroundSize: "25%", backgroundImage: `url(/images/document.svg)`} :
        { backgroundImage: `url(/images/document.svg)`}

    return (
        <>
            <DeleteModal
                elementId={props.element.Id}
                asset={props.document}
                active={showDeleteModal}
                closeModal={() => {
                    setShowDeleteModal(false);
                }}
            />
            <div className={cardStyleList[props.displayStyle]}>
                <div
                    onClick={(event) => {
                        getOnClickHandler(event);
                    }}
                    data-content={props.remaining}
                    className={documentStyleList[props.displayStyle]}
                    style={documentImageStyle}
                />
                {userType !== LoggedInUserType.DOCUMENT_COLLECTOR ? (
                    <div
                        className={styles.deleteIcon}
                        style={{ cursor: cursorStyle }}
                    >
                        <a target="_blank" rel="noreferrer" onClick={onDelete}>
                            <img alt={"rollover"} src={deleteSvg} />
                        </a>
                    </div>
                ) : <></>}
                <div className={styles.viewIcon}>
                    <a target="_blank" rel="noreferrer" onClick={onView}>
                        <img
                            alt={"rollover"}
                            src="/images/Roll-Over-View.svg"
                        />
                    </a>
                </div>
                <div className={styles.downloadIcon}>
                    <a className="documentDownload" target="_blank" rel="noreferrer" onClick={onDownload}>
                        <img
                            alt={"rollover"}
                            src="/images/Roll-Over-Download.svg"
                        />
                    </a>
                </div>
                {documentName()}
            </div>
            {addedByFooter()}
        </>
    );
}
